import log from 'loglevel';

import logProviderFactory from 'utils/logger/logProviderFactory';

import { CSRF_LOCAL_STORAGE_KEY } from '../constants/session';
import { ensureAwsWafTokenForWebSocket } from '../lib/security/awsWaf';
import type {
    SubscribedDTO,
    WsChannel,
    WsCommand,
    WsErrorDTO,
    WsMessage,
    WsResponse,
    WsSubscribeParams,
    WsSubscriberCallback,
    WsSubscription,
    WsUnsubscribeCallback,
    WsUpdateSubscriptionAction,
    WsUpdateSubscriptionParams,
} from '../models/ws';
import { arraysAreEqual } from '../utils/arrays';
import config from '../utils/config';
import { getValidFrontendSessionId } from '../utils/frontendSession';
import { getLocalStorageWithExpiration } from '../utils/storage';
import { WsMonitor } from './wsMonitor';

//TODO: Remove this suppression of non-null assertion

// Maximum delay for exponential backoff (2 ** MBE seconds)
const MAXIMUM_BACKOFF_EXPONENT = 5;

// Allowed time between command and response (milliseconds)
// Todo: think about another way to deal with this without using delays
const RESPONSE_DELAY = 5000;
const CONNECTION_STATE_CALLBACK_TIMEOUT_MS = 0;

// TODO: Replace client-side inactivity detection with BE healthchecks/heartbeats when available.
// Freshness telemetry and the backend heartbeat dependency: docs/websocket-freshness.md.
export const INACTIVITY_TIMEOUT = 60_000; // 60 seconds
const INACTIVITY_CHECK_INTERVAL = 1_000; // 1 second

// Application close code (4000–4999 range) for the client's own inactivity
// recycle. Zero messages arrived on the old socket, so client state cannot
// have drifted — connection-state listeners can skip the REST resync they run
// for real disconnects (network drops, server closes, offline).
export const WS_CLOSE_CODE_INACTIVITY_RECYCLE = 4002;

export type WsConnectionState = {
    connected: boolean;
    code?: number;
    reason?: string;
};

export type WsConnectionStateCallback = (state: WsConnectionState) => void;

export type WsClientLabel = 'event-contract' | 'legacy-exchange' | 'margin' | 'default';

const READY_STATE_NAMES: Record<number, string> = {
    0: 'CONNECTING',
    1: 'OPEN',
    2: 'CLOSING',
    3: 'CLOSED',
};

const describeReadyState = (readyState: number): string => READY_STATE_NAMES[readyState] ?? `UNKNOWN(${readyState})`;

type ManagedWsSubscription = WsSubscription & {
    clientSubscriptionId: number;
};

export type FoundSubscription =
    | { state: 'confirmed'; sid: number; params: WsSubscribeParams }
    | { state: 'pending'; sid: null; params: WsSubscribeParams }
    | { state: 'deferred'; sid: null; params: WsSubscribeParams };

class WsService {
    // Single websocket instance owned by this service. If not connected, the
    // service is actively trying to reconnect.
    private conn: WebSocket | null = null;
    public readonly monitor: WsMonitor;
    constructor(
        private wsUrl?: string,
        private readonly clientLabel: WsClientLabel = 'default',
    ) {
        this.monitor = new WsMonitor(clientLabel);
    }
    private reconnectionTimer: NodeJS.Timeout | null = null;
    private shouldReconnect = true;
    private intentionalClose = false;
    private lastMessageAt: number | null = null;
    private firstMessageStartedAt: number | null = null;
    private recoveringFromStaleClose = false;
    private subscriptionSentAt = new Map<number, number>();
    // When the current socket last fired `error`. A WebSocket `error` is always
    // followed by `close`, and only the close event carries a code, so the close
    // handler reads this to report the two as one failure.
    private lastErrorAt: number | null = null;
    private connectionStateSubscribers = new Set<WsConnectionStateCallback>();

    // Inactivity detection
    private inactivityCheckInterval: NodeJS.Timeout | null = null;
    private offlineHandler: (() => void) | null = null;

    // map from Command ID to subscription response timeout timer
    private subscriptionResponseTimers = new Map<number, number>();

    // incremented for each command sent
    private nextCommandId = 1;
    private nextClientSubscriptionId = 1;
    private backoffExponent = 0;

    // ephemeral map from Command ID to subscription, pending server acknowledgement
    private pendingSubscriptions = new Map<number, ManagedWsSubscription>();
    // Active subscription intent whose command received an error response. It is
    // retained for the next connection, but is no longer treated as in flight.
    private deferredSubscriptions = new Map<number, ManagedWsSubscription>();
    // subscriptions we have lost interest in before receiving subscription confirmation
    private pendingUnsubscriptions = new Set<ManagedWsSubscription>();
    // sids we've sent `unsubscribe` for and are awaiting the server's `unsubscribed` ack.
    // Tracked so callers (matchingSubscription, findSubscriptionBy*) treat the entry as
    // already-gone — preventing a re-subscribe from racing the in-flight unsubscribe and
    // tripping a server-side `code:6 Already subscribed`.
    private pendingUnsubAcks = new Set<number>();

    // map from Subscription ID to subscription
    private subscriptions = new Map<number, ManagedWsSubscription>();
    private pendingConnect: Promise<void> | null = null;
    private connectGeneration = 0;

    /**
     * @function connect
     * Externally available connection initiator. Will attempt to connect with exponential backoff.
     */
    connect(): void {
        if (
            this.conn == null ||
            this.conn.readyState === WebSocket.CLOSED ||
            this.conn.readyState === WebSocket.CLOSING
        ) {
            if (!this.pendingConnect) {
                const connectGeneration = ++this.connectGeneration;
                const pendingConnect = this.openConnection(connectGeneration).finally(() => {
                    if (this.pendingConnect === pendingConnect) {
                        this.pendingConnect = null;
                    }
                });
                this.pendingConnect = pendingConnect;
            }
        }
    }

    private async openConnection(connectGeneration: number): Promise<void> {
        // Clear any subscription response timers from a previous connection
        // to prevent stale timeouts from closing the new socket.
        this.subscriptionResponseTimers.forEach((timeoutId) => clearTimeout(timeoutId));
        this.subscriptionResponseTimers.clear();
        this.subscriptionSentAt.clear();
        this.firstMessageStartedAt = null;

        this.intentionalClose = false;
        this.shouldReconnect = true;
        const csrfToken = getLocalStorageWithExpiration(CSRF_LOCAL_STORAGE_KEY) || '';

        const feSessionID = getValidFrontendSessionId();

        const url = new URL(this.wsUrl ?? config.exchangeWsUrl);
        url.searchParams.set('csrfToken', csrfToken);
        if (feSessionID) {
            url.searchParams.set('frontend_session', feSessionID);
        }

        await ensureAwsWafTokenForWebSocket(url.toString());

        if (connectGeneration !== this.connectGeneration || this.intentionalClose || !this.shouldReconnect) {
            return;
        }

        this.conn = new WebSocket(url.toString());
        const currentSocket = this.conn; // guard toprevent stale handlers from mutating the current socket state
        const socketHost = url.host; // host only — the query string carries the CSRF and session tokens
        // Whether this socket ever reached OPEN. `readyState` cannot answer that
        // from inside `onerror`: "fail the WebSocket connection" moves the socket
        // to CLOSED *before* dispatching `error`, so a rejected handshake and a
        // mid-stream drop both read CLOSED there.
        let socketOpened = false;
        this.lastErrorAt = null;

        this.conn.onmessage = (evt: MessageEvent): void => {
            if (this.conn !== currentSocket) return;
            if (this.firstMessageStartedAt !== null) {
                this.monitor.trackDuration('ws_time_to_first_message', this.firstMessageStartedAt);
                this.firstMessageStartedAt = null;
            }
            this.handleEvent(evt);
        };

        this.conn.onopen = (): void => {
            if (this.conn !== currentSocket) return;
            console.warn(`[WEBSOCKETS ${this.clientLabel}] - Socket connected to ${socketHost}`);
            socketOpened = true;
            this.lastMessageAt = Date.now();
            this.firstMessageStartedAt = performance.now();
            this.lastErrorAt = null;
            this.monitor.onConnect();
            if (this.recoveringFromStaleClose) {
                this.recoveringFromStaleClose = false;
                this.monitor.onStaleRecovery();
            }
            this.monitor.resetChannelTracking();
            this.emitConnectionState({ connected: true });
            if (this.reconnectionTimer !== null) {
                clearTimeout(this.reconnectionTimer);
            }

            this.nextCommandId = 1;
            this.backoffExponent = 0;
            this.pendingUnsubscriptions = new Set<ManagedWsSubscription>();
            this.pendingUnsubAcks.clear();
            this.resubscribe();
        };

        this.conn.onclose = (e): void => {
            if (this.conn !== currentSocket) return;
            console.warn(`[WEBSOCKETS ${this.clientLabel}] - Socket closed`, {
                host: socketHost,
                code: e.code,
                reason: e.reason || '(none)',
                wasClean: e.wasClean,
                // A preceding `error` means the failure was transport-level rather
                // than a close frame from the server, so the code is browser-issued
                // (1006 for an abnormal close with no frame at all).
                msSincePrecedingError: this.lastErrorAt === null ? null : Date.now() - this.lastErrorAt,
            });
            this.lastErrorAt = null;
            this.monitor.onDisconnect(e.code, e.reason);
            this.subscriptionResponseTimers.forEach((timeoutId) => clearTimeout(timeoutId));
            this.subscriptionResponseTimers.clear();
            this.subscriptionSentAt.clear();
            this.firstMessageStartedAt = null;
            this.lastMessageAt = null;
            const shouldReconnect = this.shouldReconnect && !this.intentionalClose;
            this.intentionalClose = false;
            if (!shouldReconnect) {
                this.emitConnectionState({ connected: false, code: e.code, reason: e.reason });
                return;
            }
            this.emitConnectionState({ connected: false, code: e.code, reason: e.reason });
            console.warn(`[WEBSOCKETS ${this.clientLabel}] - Will attempt to reconnect to websocket`);
            const { delay, random_ms, next_exp } = this.exponentialBackoff(this.backoffExponent);
            this.backoffExponent = next_exp;
            if (this.reconnectionTimer) {
                clearTimeout(this.reconnectionTimer);
            }
            this.reconnectionTimer = setTimeout(() => this.connect(), delay + random_ms);
        };

        // Browsers dispatch a bare Event here, never an ErrorEvent: the spec
        // withholds the cause so a page cannot probe the network with a socket.
        // `message`/`filename`/`error` are therefore always undefined, and the
        // only detail worth logging is our own — which client failed and how far
        // the handshake got. The cause, as far as it is knowable, arrives with
        // the `close` event that always follows this one.
        this.conn.onerror = (event): void => {
            if (this.conn !== currentSocket) return;
            this.lastErrorAt = Date.now();
            const readyState = describeReadyState(currentSocket.readyState);
            // `handshake` means the socket never opened — DNS, TLS, the WAF or a
            // proxy rejected it. `established` means an open socket dropped
            // mid-stream. They call for different investigations. Handshake
            // failures are logged at warn: they're usually client-network/WAF
            // noise and self-heal via the reconnect backoff (same error→warn
            // downgrade PR #7709 applied to JSON parse errors to cut RUM noise).
            const phase = socketOpened ? 'established' : 'handshake';
            const logAtLevel = phase === 'handshake' ? log.warn : log.error;
            logAtLevel(`[WEBSOCKETS ${this.clientLabel}] - Socket error (cause unavailable, see the close below)`, {
                host: socketHost,
                type: event.type,
                phase,
                readyState,
            });
            this.monitor.onError(`${event.type} during ${phase}, readyState ${readyState}`);
        };

        if (this.offlineHandler) {
            window.removeEventListener('offline', this.offlineHandler);
            this.offlineHandler = null;
        }

        this.offlineHandler = () => {
            if (this.conn === currentSocket) {
                this.close(4001, 'Browser offline');
            }
        };
        window.addEventListener('offline', this.offlineHandler);

        this.startInactivityCheck();
    }

    /*
      4000–4999 is the range safe for application usage
     */
    close(code: number | undefined = 4000, reason: string, options?: { intentional?: boolean }): void {
        if (this.offlineHandler) {
            window.removeEventListener('offline', this.offlineHandler);
            this.offlineHandler = null;
        }
        this.stopInactivityCheck();
        const closeCode = code ?? 4000;
        if (options?.intentional) {
            this.recoveringFromStaleClose = false;
            this.intentionalClose = true;
            this.shouldReconnect = false;
            this.connectGeneration += 1;
            this.pendingConnect = null;
        } else {
            this.intentionalClose = false;
            this.shouldReconnect = true;
        }
        this.subscriptionResponseTimers.forEach((timeoutId) => clearTimeout(timeoutId));
        this.subscriptionResponseTimers.clear();
        this.subscriptionSentAt.clear();
        this.firstMessageStartedAt = null;

        if (this.reconnectionTimer) {
            clearTimeout(this.reconnectionTimer);
            this.reconnectionTimer = null;
        }

        if (this.conn !== null) {
            if (this.conn.readyState === WebSocket.OPEN || this.conn.readyState === WebSocket.CONNECTING) {
                this.conn.close(closeCode, reason);
            }
        }
    }

    dispose(): void {
        this.close(1000, 'Closing websockets because of client disposal', { intentional: true });
    }

    /**
     * @function subscribe
     * Subscribes interested parties to a channel, and provides a callback to unsubscribe.
     */
    subscribe(params: WsSubscribeParams, subscriber: WsSubscriberCallback): WsUnsubscribeCallback {
        return this.subscribeInternal(params, subscriber);
    }

    private subscribeInternal(
        params: WsSubscribeParams,
        subscriber: WsSubscriberCallback,
        clientSubscriptionId?: number,
    ): WsUnsubscribeCallback {
        console.warn('[WEBSOCKETS] - Subscribing', {
            params: this.subscribeParamsSummary(params),
            subscriber: this.subscriberLabel(subscriber),
        });
        // to be captured in unsubscribeCallback closure
        let subscription: ManagedWsSubscription;

        const existingSubscription = this.matchingSubscription(params);
        if (existingSubscription != null) {
            // the requested subscription already exists, and this subscriber just needs to enroll
            subscription = existingSubscription;
            console.warn('[WEBSOCKETS] - Found existing subscription', {
                subscription: this.subscriptionSummary(subscription),
            });
            subscription.subscribers.add(subscriber);
        } else {
            // the requested subscription does not exist, and this subscriber needs to found it
            const commandId = this.nextCommandId++;
            const command: WsCommand = {
                id: commandId,
                cmd: 'subscribe',
                params: params,
            };
            subscription = this.createSubscription(params, subscriber, clientSubscriptionId);
            this.pendingSubscriptions.set(commandId, subscription);
            if (this?.conn?.readyState === WebSocket.OPEN) {
                // Set timeout BEFORE send so handleEvent can clear it
                // if the server (or mock) responds synchronously.
                const timeoutId = window.setTimeout(
                    this.handleSubscriptionResponseTimeout,
                    RESPONSE_DELAY,
                    commandId,
                    this.conn,
                    subscription,
                );
                console.warn('[WEBSOCKETS] - Setting subscription timeout', {
                    commandId,
                    timeoutId,
                    subscription: this.subscriptionSummary(subscription),
                });
                this.subscriptionResponseTimers.set(commandId, timeoutId);
                console.warn('[WEBSOCKETS] - Effectively sending subscription message', JSON.stringify(command));
                this.subscriptionSentAt.set(commandId, performance.now());
                this.conn?.send(JSON.stringify(command));
            }
        }

        // callback used to unsubscribe
        return (): boolean => {
            const resolvedSubscription = this.resolveSubscriptionForUnsubscribe(subscription, subscriber);
            if (!resolvedSubscription) {
                return false;
            }
            if (!resolvedSubscription.subscribers.has(subscriber)) {
                return false;
            }
            console.warn(
                '[WEBSOCKETS] - Deleting subscriber',
                this.subscriberLabel(subscriber),
                'from subscription',
                this.subscriptionSummary(resolvedSubscription),
                'subscriber length before deleting',
                resolvedSubscription.subscribers.size,
            );
            resolvedSubscription.subscribers.delete(subscriber);
            if (resolvedSubscription.subscribers.size === 0) {
                return this.unsubscribe(resolvedSubscription);
            }
            return false;
        };
    }

    /**
     * @function handleEvent
     * Utilized by the @function connect to start listening to messages once the connection is established.
     */
    private handleEvent(evt: MessageEvent): void {
        this.lastMessageAt = Date.now();

        let response: WsResponse;
        try {
            response = JSON.parse(evt.data);
        } catch (error) {
            const raw = typeof evt.data === 'string' ? evt.data : '';
            console.warn('[WEBSOCKETS] - Failed to parse message', error);
            this.monitor.track('ws_parse_error', {
                errorMessage: String(error),
                messageLength: raw.length,
                messagePreview: raw.slice(0, 200),
            });
            return;
        }
        const messages = Array.isArray(response) ? (response as WsMessage[]) : [response as WsMessage];

        for (const message of messages) {
            this.handleMessage(message);
        }
    }

    private handleMessage(message: WsMessage): void {
        log.trace('[WEBSOCKETS] - Received WS message ', message.type, 'with payload', JSON.stringify(message));

        switch (message.type) {
            case undefined:
                break;
            case 'subscribed': {
                const messageId = message.id;
                if (messageId == null) {
                    console.warn('[WEBSOCKETS] - Subscribed message missing command id', message);
                    break;
                }

                // stop waiting for a response
                // A multi-channel subscribe may produce an error for one channel
                // followed by a successful ack for another using the same command id.
                const newSubscription =
                    this.pendingSubscriptions.get(messageId) ?? this.deferredSubscriptions.get(messageId);
                if (!newSubscription) {
                    console.warn('[WEBSOCKETS] - Ignoring subscribed response without active subscription', messageId);
                    this.clearSubscriptionResponseTimer(messageId, 'stale subscribed ack');
                    this.subscriptionSentAt.delete(messageId);
                    break;
                }

                const payload = message.msg as SubscribedDTO;
                const sentAt = this.subscriptionSentAt.get(messageId);
                if (sentAt !== undefined) {
                    this.subscriptionSentAt.delete(messageId);
                    this.monitor.trackDuration('ws_subscribe_ack', sentAt, {
                        channels: newSubscription.params.channels,
                    });
                }
                this.clearSubscriptionResponseTimer(messageId, 'subscribed ack');
                // mark the subscription as open, and index it with its SID
                console.warn('[WEBSOCKETS] - New subscription confirmed', this.subscriptionSummary(newSubscription));
                console.warn(
                    '[WEBSOCKETS] - A subscription will be added with subscriptions keys',
                    Array.from(this.subscriptions.keys()),
                );
                this.subscriptions.set(payload.sid, newSubscription);
                this.pendingSubscriptions.delete(messageId);
                this.deferredSubscriptions.delete(messageId);

                // check if we were waiting to unsubscribe
                if (this.pendingUnsubscriptions.delete(newSubscription)) {
                    this.unsubscribe(newSubscription);
                }
                break;
            }
            case 'unsubscribed':
                // remove subscription
                const oldSubscription = this.subscriptions.get(message.sid!)!;
                if (oldSubscription !== undefined) {
                    const deleted = this.subscriptions.delete(message.sid!);
                    this.pendingUnsubAcks.delete(message.sid!);
                    console.warn('[WEBSOCKETS] - Deleting subscription on unsubscribed message', message.sid, deleted);
                    // if server forced un-subscription or if there was other subscribers
                    // after we sent the unsubscribe message, we have to resubscribe in this case
                    if (oldSubscription.subscribers.size > 0) {
                        for (const subscriber of oldSubscription.subscribers) {
                            this.subscribeInternal(
                                oldSubscription.params,
                                subscriber,
                                oldSubscription.clientSubscriptionId,
                            );
                        }
                    }
                }
                break;
            case 'error': {
                const messageId = message.id;
                const failedSubscription =
                    messageId === undefined
                        ? undefined
                        : (this.pendingSubscriptions.get(messageId) ?? this.deferredSubscriptions.get(messageId));
                if (messageId !== undefined) {
                    this.clearSubscriptionResponseTimer(messageId, 'error response');
                    if (failedSubscription) {
                        this.pendingSubscriptions.delete(messageId);
                        this.deferredSubscriptions.delete(messageId);
                        this.pendingUnsubscriptions.delete(failedSubscription);
                        if (failedSubscription.subscribers.size > 0) {
                            this.deferredSubscriptions.set(messageId, failedSubscription);
                        } else {
                            this.subscriptionSentAt.delete(messageId);
                        }
                    }
                }

                const payload = message.msg as WsErrorDTO;
                this.monitor.track('ws_command_error', {
                    commandId: messageId,
                    code: payload.code,
                    serverMessage: payload.msg,
                    subscription: failedSubscription ? this.subscriptionSummary(failedSubscription) : undefined,
                });
                const error = `[WEBSOCKETS] - Received error message: ${JSON.stringify(message)}`;
                console.error(error);
                logProviderFactory.logError(error);
                break;
            }
            default:
                // Track channel message for monitoring
                if (message.type) {
                    this.monitor.onChannelMessage(message.type);
                }
                // send channel message to appropriate subscriber(s)
                this.subscriptions.get(message.sid!)?.subscribers.forEach((subCallback) => {
                    subCallback(message);
                });
                break;
        }
    }

    /**
     * @function resubscribe
     * Utilized by the @function connect to catch up to subscription requests when connection is opened.
     */
    resubscribe = (): void => {
        const prevSubscriptions: ManagedWsSubscription[] = [
            ...this.pendingSubscriptions.values(),
            ...this.deferredSubscriptions.values(),
            ...this.subscriptions.values(),
        ];

        this.pendingSubscriptions.clear();
        this.deferredSubscriptions.clear();
        this.subscriptions.clear();

        console.warn(
            '[WEBSOCKETS] - Resubscribing for previous subscriptions',
            prevSubscriptions.map((subscription) => this.subscriptionSummary(subscription)),
        );

        for (const subscription of prevSubscriptions) {
            for (const subscriber of subscription.subscribers) {
                this.subscribeInternal(subscription.params, subscriber, subscription.clientSubscriptionId);
            }
        }
    };

    /**
     * @function unsubscribe
     * Utilized by the callback returned in the @function connect to handle unsubscription logic.
     * Returns true if the subscription was successfully unsubscribed from, false otherwise.
     */
    unsubscribe = (subscription: ManagedWsSubscription): boolean => {
        console.warn('[WEBSOCKETS] - Trying to unsubscribe from', this.subscriptionSummary(subscription));
        for (const [sid, sub] of this.subscriptions) {
            if (sub === subscription) {
                console.warn('[WEBSOCKETS] - Found existing subscription to unsubscribe', {
                    sid,
                    subscription: this.subscriptionSummary(sub),
                });
                if (subscription.subscribers.size === 0) {
                    console.warn(
                        '[WEBSOCKETS] - Can proceed and unsubscribe from as no one else is listening',
                        this.subscriptionSummary(subscription),
                    );
                    // Mark sid as awaiting `unsubscribed` ack rather than deleting the
                    // subscription locally. The deletion happens when the ack lands. Any
                    // subscribe that arrives during this window (re-watch race) sees the
                    // sid in `pendingUnsubAcks`, treats it as gone, and sends a fresh
                    // subscribe. Both commands hit the server in FIFO order, so the server
                    // processes the unsubscribe before the new subscribe — no `Already
                    // subscribed` error.
                    this.pendingUnsubAcks.add(sid);
                    this.nextCommandId++;
                    this.sendUnsubscribeMessage(sid, this.nextCommandId);
                    return true;
                }
                return false;
            }
        }

        for (const [cid, sub] of this.pendingSubscriptions) {
            // still awaiting subscription confirmation
            if (sub === subscription) {
                if (!this.subscriptionResponseTimers.has(cid)) {
                    this.pendingSubscriptions.delete(cid);
                    this.subscriptionSentAt.delete(cid);
                    this.pendingUnsubscriptions.delete(subscription);
                    return true;
                }
                console.warn(
                    "[WEBSOCKETS] - Can't unsubscribe as we are still awaiting for subscribed ack for subscription",
                    this.subscriptionSummary(sub),
                );
                this.pendingUnsubscriptions.add(subscription);
                console.warn(
                    '[WEBSOCKETS] - Adding subscription with cid',
                    cid,
                    'to pendingUnsubscriptions',
                    this.pendingUnsubscriptions.size,
                );
                return true;
            }
        }

        for (const [cid, sub] of this.deferredSubscriptions) {
            if (sub === subscription) {
                this.deferredSubscriptions.delete(cid);
                this.subscriptionSentAt.delete(cid);
                return true;
            }
        }

        return false;
    };

    private resolveSubscriptionForUnsubscribe(
        capturedSubscription: ManagedWsSubscription,
        subscriber: WsSubscriberCallback,
    ): ManagedWsSubscription | null {
        for (const sub of this.subscriptions.values()) {
            if (sub === capturedSubscription) {
                return sub;
            }
        }
        for (const sub of this.pendingSubscriptions.values()) {
            if (sub === capturedSubscription) {
                return sub;
            }
        }
        for (const sub of this.deferredSubscriptions.values()) {
            if (sub === capturedSubscription) {
                return sub;
            }
        }

        const subscriptionByClientId = this.findSubscriptionByClientSubscriptionId(
            capturedSubscription.clientSubscriptionId,
            subscriber,
        );
        if (subscriptionByClientId) {
            return subscriptionByClientId;
        }

        const capturedKey = this.subscriptionKey(capturedSubscription.params);

        for (const [sid, sub] of this.subscriptions) {
            if (this.pendingUnsubAcks.has(sid)) continue;
            if (this.subscriptionKey(sub.params) === capturedKey && sub.subscribers.has(subscriber)) {
                return sub;
            }
        }
        for (const sub of this.pendingSubscriptions.values()) {
            if (this.subscriptionKey(sub.params) === capturedKey && sub.subscribers.has(subscriber)) {
                return sub;
            }
        }
        for (const sub of this.deferredSubscriptions.values()) {
            if (this.subscriptionKey(sub.params) === capturedKey && sub.subscribers.has(subscriber)) {
                return sub;
            }
        }

        return null;
    }

    /**
     * @function unsubscribeWithBackoff
     * Send an unsubscribe command if the connection is open.
     */
    sendUnsubscribeMessage = (sid: number, cid: number): void => {
        const command: WsCommand = {
            id: cid,
            cmd: 'unsubscribe',
            params: { sids: [sid] },
        };

        /*
          We should try our best to notify the server that we don't need this subscription anymore.
          But if the connection is not open we can't keep sending the unsubscribe command as we
          can unsubscribe to a different subscription using the same sid as another connection
          can be created in between and this connection can use the same sid we have as parameter
          value inside this function.
         */
        if (this.conn?.readyState === WebSocket.OPEN) {
            log.trace('[WEBSOCKETS] - Effectively sending unsubscription message', JSON.stringify(command));
            this.conn?.send(JSON.stringify(command));
            return;
        }
    };

    private handleSubscriptionResponseTimeout = (
        commandId: number,
        commandSocket: WebSocket,
        expectedSubscription: ManagedWsSubscription,
    ): void => {
        const pendingSubscription = this.pendingSubscriptions.get(commandId);
        if (
            this.conn !== commandSocket ||
            pendingSubscription !== expectedSubscription ||
            !this.subscriptionResponseTimers.has(commandId)
        ) {
            console.warn('[WEBSOCKETS] - Ignoring stale subscription timeout', {
                commandId,
                expectedSubscription: this.subscriptionSummary(expectedSubscription),
                pendingSubscription: pendingSubscription ? this.subscriptionSummary(pendingSubscription) : null,
            });
            return;
        }

        this.subscriptionResponseTimers.delete(commandId);
        console.warn('[WEBSOCKETS] - Subscription response timeout', {
            commandId,
            subscription: this.subscriptionSummary(expectedSubscription),
        });
        this.monitor.onSubscriptionTimeout();
        this.close(undefined, 'Closing websockets connection because of subscription response timeout');
    };

    private clearSubscriptionResponseTimer(commandId: number, reason: string): void {
        const timeoutId = this.subscriptionResponseTimers.get(commandId);
        if (!timeoutId) {
            return;
        }

        console.warn('[WEBSOCKETS] - Erasing subscription timeout', {
            commandId,
            reason,
            timeoutId,
        });
        clearTimeout(timeoutId);
        this.subscriptionResponseTimers.delete(commandId);
    }

    private createSubscription(
        params: WsSubscribeParams,
        subscriber: WsSubscriberCallback,
        clientSubscriptionId = this.nextClientSubscriptionId++,
    ): ManagedWsSubscription {
        return {
            params,
            subscribers: new Set([subscriber]),
            clientSubscriptionId,
        };
    }

    private findSubscriptionByClientSubscriptionId(
        clientSubscriptionId: number,
        subscriber: WsSubscriberCallback,
    ): ManagedWsSubscription | null {
        for (const [sid, sub] of this.subscriptions) {
            if (this.pendingUnsubAcks.has(sid)) continue;
            if (sub.clientSubscriptionId === clientSubscriptionId && sub.subscribers.has(subscriber)) {
                return sub;
            }
        }
        for (const sub of this.pendingSubscriptions.values()) {
            if (sub.clientSubscriptionId === clientSubscriptionId && sub.subscribers.has(subscriber)) {
                return sub;
            }
        }
        for (const sub of this.deferredSubscriptions.values()) {
            if (sub.clientSubscriptionId === clientSubscriptionId && sub.subscribers.has(subscriber)) {
                return sub;
            }
        }
        return null;
    }

    private subscriberLabel(subscriber: WsSubscriberCallback): string {
        return subscriber.name || 'anonymous';
    }

    private subscribeParamsSummary(params: WsSubscribeParams): Record<string, unknown> {
        const summary: Record<string, unknown> = {
            channels: [...params.channels],
        };

        if (params.market_id !== undefined) summary.market_id = params.market_id;
        if (params.market_ids !== undefined) summary.market_ids = [...params.market_ids];
        if (params.market_tickers !== undefined) summary.market_tickers = [...params.market_tickers];
        if (params.index_ids !== undefined) summary.index_ids = [...params.index_ids];
        if (params.send_initial_snapshot !== undefined) summary.send_initial_snapshot = params.send_initial_snapshot;
        if (params.subaccount !== undefined) summary.subaccount = params.subaccount;

        return summary;
    }

    private subscriptionSummary(subscription: ManagedWsSubscription): Record<string, unknown> {
        return {
            clientSubscriptionId: subscription.clientSubscriptionId,
            params: this.subscribeParamsSummary(subscription.params),
            subscriberCount: subscription.subscribers.size,
        };
    }

    private subscriptionKey(params: WsSubscribeParams): string {
        const channels = [...params.channels].sort().join(',');
        const marketId = params.market_id ?? '';
        const marketIds = params.market_ids ? [...params.market_ids].sort().join(',') : '';
        const marketTickers = params.market_tickers ? [...params.market_tickers].sort().join(',') : '';
        // `cfbenchmarks_value` selects by index rather than by market, so two charts on different
        // assets produce params that are otherwise identical. Without this they would be treated
        // as the same subscription, and the second chart's indices would never be requested while
        // its handler filtered out every frame the first one's subscription delivered.
        const indexIds = params.index_ids ? [...params.index_ids].sort().join(',') : '';
        const snapshot = params.send_initial_snapshot ? '1' : '0';
        return `${channels}|${marketId}|${marketIds}|${marketTickers}|${indexIds}|${snapshot}`;
    }

    /**
     * @function matchingSubscription
     * Utilized by the @function subscribe to search through open and requested subscriptions for matching params.
     */
    private matchingSubscription = (params: WsSubscribeParams): ManagedWsSubscription | null => {
        const key = this.subscriptionKey(params);
        for (const sub of this.pendingSubscriptions.values()) {
            if (this.subscriptionKey(sub.params) === key) {
                return sub;
            }
        }
        for (const sub of this.deferredSubscriptions.values()) {
            if (this.subscriptionKey(sub.params) === key) {
                return sub;
            }
        }
        for (const [sid, sub] of this.subscriptions) {
            if (this.pendingUnsubAcks.has(sid)) continue;
            if (this.subscriptionKey(sub.params) === key) {
                return sub;
            }
        }
        return null;
    };

    /**
     * Finds a confirmed subscription matching the given channels, ignoring ticker params.
     * Returns `{ sid, params }` or `null` if not found.
     */
    findSubscriptionByChannels = (channels: WsChannel[]): { sid: number; params: WsSubscribeParams } | null => {
        const sortedChannels = [...channels].sort().join(',');
        for (const [sid, sub] of this.subscriptions) {
            if (this.pendingUnsubAcks.has(sid)) continue;
            if ([...sub.params.channels].sort().join(',') === sortedChannels) {
                return { sid, params: sub.params };
            }
        }
        return null;
    };

    /**
     * Finds a subscription that contains the given subscriber callback. Unlike
     * `findSubscriptionByChannels`, this uniquely identifies a subscription even when
     * multiple subscriptions share the same channel.
     *
     * Surfaces confirmed, in-flight, and deferred subscription intent. Deferred
     * entries received a command error and will be replayed only when the
     * socket reconnects.
     */
    findSubscriptionBySubscriber = (subscriber: WsSubscriberCallback): FoundSubscription | null => {
        for (const [sid, sub] of this.subscriptions) {
            if (this.pendingUnsubAcks.has(sid)) continue;
            if (sub.subscribers.has(subscriber)) {
                return { state: 'confirmed', sid, params: sub.params };
            }
        }
        for (const sub of this.pendingSubscriptions.values()) {
            if (sub.subscribers.has(subscriber)) {
                return { state: 'pending', sid: null, params: sub.params };
            }
        }
        for (const sub of this.deferredSubscriptions.values()) {
            if (sub.subscribers.has(subscriber)) {
                return { state: 'deferred', sid: null, params: sub.params };
            }
        }
        return null;
    };

    /**
     * Sends `update_subscription` commands for an existing subscription.
     * Add/delete actions optimistically update the subscription's `market_tickers`.
     * Returns false if sid not found or connection not open.
     */
    updateSubscription = (
        sid: number,
        action: WsUpdateSubscriptionAction,
        tickers: string[],
        options?: { sendInitialSnapshot?: boolean },
    ): boolean => {
        if (this.conn?.readyState !== WebSocket.OPEN) return false;
        const subscription = this.subscriptions.get(sid);
        if (!subscription) return false;

        const commandId = this.nextCommandId++;
        const params: WsUpdateSubscriptionParams = {
            sid,
            action,
            market_tickers: tickers,
        };
        if (options?.sendInitialSnapshot) {
            params.send_initial_snapshot = true;
        }
        const command: WsCommand = {
            id: commandId,
            cmd: 'update_subscription',
            params,
        };
        this.conn.send(JSON.stringify(command));

        // Optimistically update local state
        const currentTickers = subscription.params.market_tickers ?? [];
        if (action === 'add_markets') {
            subscription.params = {
                ...subscription.params,
                market_tickers: [...currentTickers, ...tickers.filter((t) => !currentTickers.includes(t))],
            };
        } else if (action === 'delete_markets') {
            subscription.params = {
                ...subscription.params,
                market_tickers: currentTickers.filter((t) => !tickers.includes(t)),
            };
        }

        return true;
    };

    /**
     * @function exponentialBackoff
     * Compute values for exponential backoff.
     */
    exponentialBackoff = (exponent: number): { delay: number; random_ms: number; next_exp: number } => {
        return {
            delay: 2 ** exponent * 1000,
            random_ms: Math.floor(Math.random() * Math.floor(1000)),
            next_exp: Math.min(exponent + 1, MAXIMUM_BACKOFF_EXPONENT),
        };
    };

    // Detect if a channel is connected
    isConnected = (channel: WsChannel): boolean => {
        if (!this.isWsConnected()) {
            return false;
        }
        for (const [, sub] of this.subscriptions) {
            if (arraysAreEqual(sub.params.channels, [channel])) {
                return true;
            }
        }
        return false;
    };

    // Detect if the WS connection is open
    isWsConnected = (): boolean => {
        return this.conn?.readyState === WebSocket.OPEN;
    };

    getLastMessageAt = (): number | null => {
        return this.lastMessageAt;
    };

    subscribeConnectionState = (callback: WsConnectionStateCallback): (() => void) => {
        this.connectionStateSubscribers.add(callback);
        return (): void => {
            this.connectionStateSubscribers.delete(callback);
        };
    };

    private emitConnectionState(state: WsConnectionState): void {
        this.connectionStateSubscribers.forEach((callback) => {
            setTimeout(() => {
                try {
                    callback(state);
                } catch (error) {
                    console.error('[WEBSOCKETS] - Connection state subscriber error:', error);
                }
            }, CONNECTION_STATE_CALLBACK_TIMEOUT_MS);
        });
    }

    private startInactivityCheck(): void {
        if (this.inactivityCheckInterval) return; // already running

        this.inactivityCheckInterval = setInterval(() => {
            if (!this.isDocumentVisible()) return;

            const lastMessageAt = this.lastMessageAt;
            if (
                this.conn?.readyState === WebSocket.OPEN &&
                this.subscriptions.size > 0 &&
                lastMessageAt !== null &&
                Date.now() > lastMessageAt + INACTIVITY_TIMEOUT
            ) {
                console.warn('[WEBSOCKETS] - Inactivity detected, closing connection');
                this.recoveringFromStaleClose = true;
                this.monitor.onStaleDetected(Date.now() - lastMessageAt, this.subscriptions.size);
                this.close(WS_CLOSE_CODE_INACTIVITY_RECYCLE, 'Inactivity timeout');
            }
        }, INACTIVITY_CHECK_INTERVAL);
    }

    private isDocumentVisible(): boolean {
        if (typeof document === 'undefined') {
            return true;
        }

        return document.visibilityState === 'visible';
    }

    private stopInactivityCheck(): void {
        if (this.inactivityCheckInterval) {
            clearInterval(this.inactivityCheckInterval);
            this.inactivityCheckInterval = null;
        }
    }
}

export default WsService;

/*
    TODO:
        1. handle error messages once they're defined
*/
